import React from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import { FirstMenu } from '../../models/first-menu.entity';
import GlobalNotification from '../../component/common/snackbar';
import setLoading from '../../component/linear-determinate';
import CommonInput from '../../component/form/common-input.component';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import SubFunctionProjectSettingSaveButton from './sub-function-project-setting-save-button.component';
import FirstLevelMenuTagPicker from './first-level-menu-tag-picker.component';
import { RwaSettingPageSkeleton } from './sub-function-settings-skeletons.component';
import './scss/rwa-setting.page.scss';

/** 公网 QOP 相关授权码，与 RBP2 `QOP_AUTH` 一致，用于判断是否展示「外网」扫码选项 */
const QOP_WARRANT_CODES = ['AU1024', 'AU1025', 'AU1026'] as const;

type Props = Record<string, never>;

type State = {
    /** 全页保存中，禁用保存按钮 */
    isSaving: boolean;
    /** 一级菜单列表（含 `exact_search_rules`），用于表格行 */
    menuData: FirstMenu[];
    /** 极速点餐精确搜索规则（设置 id 23） */
    fastSearchRule: number;
    /** 就餐超时预警时间（设置 id 47，单位：分钟） */
    dinnerWarningMinutes: number;
    /** QR 扫码网络模式（设置 id 168：0 关闭 / 1 内网 / 2 外网） */
    qrScanMode: number;
    /** 外卖菜单多选（设置 id 145，存为一级菜单 id 列表） */
    takeawayMenuIds: number[];
    /** 是否具备公网 QOP 授权（决定 id 168 是否可选「外网」） */
    hasPublicQopAuth: boolean;
    /** 首包拉取完成前展示骨架 */
    initialPageDataReady: boolean;
};

/**
 * 云 RBP「子功能管理」— RWA 设置：一级菜单搜索规则、基础设置与外卖菜单；接口与 RBP2 `project-setting-rwa` 对齐，走 `apiManage_rbp`。
 * 布局按 Figma MCP `get_design_context`（file `0xkyur5MwXrYbyfTUf8tF9`，node `1893:21866`）Outlet 区：双列白卡、左列默认规则+菜单行、右列基础设置+外卖菜单（`FirstLevelMenuTagPicker` 与 KWA 同交互）。隧道校验在子功能壳页 `HookDecorator` 中统一完成。
 */
class RwaSettingPage extends React.Component<Props, State> {
    /** 顶栏「辅助模式」：控制 Figma 次要说明展示（node 1893:21866） */
    static contextType = RbpCloudSubFunctionAuxiliaryModeContext;
    /** 由 `contextType` 注入 */
    context!: React.ContextType<typeof RbpCloudSubFunctionAuxiliaryModeContext>;

    /** 防止保存请求重叠 */
    private saveLocked = false;

    public constructor(props: Props) {
        super(props);
        this.state = {
            isSaving: false,
            menuData: [],
            fastSearchRule: 1,
            dinnerWarningMinutes: 0,
            qrScanMode: 1,
            takeawayMenuIds: [],
            hasPublicQopAuth: false,
            initialPageDataReady: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        await this.loadPageData();
    }

    /**
     * 拉取菜单、设置 23/47/168/145 及 QOP 授权判断，失败时 Toast。
     */
    private loadPageData = async (): Promise<void> => {
        try {
            const hasPublicQopAuth = await this.refreshPublicQopAuth();
            const menuRes = await apiManage_rbp.get_menus_v3({ type: 0, with_dish: false });
            let menuData: FirstMenu[] = [];
            if (_.get(menuRes, 'error')) {
                if (_.get(menuRes, 'message') !== 'canceled') {
                    GlobalNotification.error(i18n.t('kwa_setting_page_get_first_level_menu_failed'));
                }
            } else {
                menuData = (menuRes as FirstMenu[]).map((m) => ({ ...m }));
            }
            const api23 = apiManage_rbp.getSettingById(23);
            const api47 = apiManage_rbp.getSettingById(47);
            const api168 = apiManage_rbp.getSettingById(168);
            const api145 = apiManage_rbp.getSettingById(145);
            const values = await Promise.all([api23, api47, api168, api145]);
            let fastSearchRule = 1;
            let dinnerWarningMinutes = 0;
            let qrScanMode = 1;
            let takeawayMenuIds: number[] = [];
            for (const res of values) {
                const resId = _.get(res, '[0].id');
                if (resId === 23) {
                    fastSearchRule = Number(Number(_.get(res, '[0].value')).toFixed(2));
                } else if (resId === 47) {
                    dinnerWarningMinutes = Number(Number(_.get(res, '[0].value.mins')).toFixed(2));
                } else if (resId === 168) {
                    qrScanMode = Number(Number(_.get(res, '[0].value')).toFixed(2));
                    if (!hasPublicQopAuth && qrScanMode === 2) {
                        qrScanMode = 1;
                    }
                } else if (resId === 145) {
                    const raw = String(_.get(res, '[0].value') || '');
                    takeawayMenuIds = raw.split(',').filter(Boolean).map(Number);
                }
            }
            this.setState({
                menuData,
                fastSearchRule,
                dinnerWarningMinutes,
                qrScanMode,
                takeawayMenuIds,
            });
        } finally {
            this.setState({ initialPageDataReady: true });
        }
    };

    /**
     * 根据 Smartway Cloud 密钥与 warrants 判断是否具备公网 QOP 能力（与 RBP2 `getAuthoQop` 一致）。
     * @returns 是否具备外网扫码授权
     */
    private refreshPublicQopAuth = async (): Promise<boolean> => {
        let hasPublicQopAuth = false;
        const api45 = await apiManage_rbp.getSettingById(45);
        const swCloudKey = String(_.get(api45, '[0].value', '') || '').trim();
        if (swCloudKey) {
            const verifyRes = await apiManage_rbp.getSmartwayCloudKey(swCloudKey);
            const stringId = _.get(verifyRes, ['restaurant', 'string_id']);
            if (stringId) {
                const data = await apiManage_rbp.getAuthorizationMessage();
                const authorizationMessage = _.get(data, 'warrant_data', '');
                if (_.isArray(authorizationMessage) && authorizationMessage.length > 0) {
                    hasPublicQopAuth = !!authorizationMessage.find((item: { code?: string }) =>
                        QOP_WARRANT_CODES.includes(item?.code as (typeof QOP_WARRANT_CODES)[number]),
                    );
                }
            }
        }
        this.setState({ hasPublicQopAuth });
        return hasPublicQopAuth;
    };

    /**
     * 组装设置 id 168 的下拉项；无公网授权时隐藏「外网」。
     */
    private getQrScanSelectItems(): { value: number; label: string }[] {
        const all = [
            { value: 0, label: i18n.t('cash_drop_page_off') },
            { value: 1, label: i18n.t('qop_setting_page_local_network') },
            { value: 2, label: i18n.t('qop_setting_page_data_network') },
        ];
        if (!this.state.hasPublicQopAuth) {
            return all.filter((n) => n.value !== 2);
        }
        return all;
    }

    /**
     * 极速点餐规则下拉项（与 RBP2 `ruleSelectArr` 一致；`label` 须为 i18n key，因 `BaseInput` 会对 `MenuItem` 调用 `i18n.t`）。
     */
    private getRuleSelectItems(): { value: number; label: string }[] {
        return [
            { value: 1, label: 'rwa_setting_page_search_rule_minus' },
            { value: 2, label: 'rwa_setting_page_search_rule_dot_minus' },
            { value: 3, label: 'rwa_setting_page_search_rule_nlu' },
        ];
    }

    /**
     * 保存：批量 PATCH 菜单搜索规则、设置 23/47/145/168。
     */
    private persistSettings = async (): Promise<void> => {
        const { menuData, fastSearchRule, dinnerWarningMinutes, qrScanMode, takeawayMenuIds } = this.state;
        setLoading(true);
        try {
            const menuParams = menuData.map((n) => ({
                exact_search_rules: n.exact_search_rules as number,
                main_group_id: n.main_group_id as number,
            }));
    
            const menuPatchRes = apiManage_rbp.patch_first_menu_search_rules(menuParams);
            const api23 = apiManage_rbp.patchSettingById(23, { stringValue: String(fastSearchRule) });
            const api47 = apiManage_rbp.patchSettingById(47, { mins: String(dinnerWarningMinutes) });
            const api145 = apiManage_rbp.patchSettingById(145, { stringValue: takeawayMenuIds.join(',') });
            const api168 = apiManage_rbp.patchSettingById(168, { stringValue: String(qrScanMode) });
            const results = await Promise.all([api23, api47, api145, api168, menuPatchRes]);
            const failed = results.some((res) => _.get(res, 'error'));
            if (failed) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            } else {
                GlobalNotification.success(i18n.t('global_text_operate_success'));
            }
        } catch {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * 点击保存：带锁避免重复提交。
     */
    private onClickSave = async (): Promise<void> => {
        if (this.saveLocked || this.state.isSaving) {
            return;
        }
        this.saveLocked = true;
        this.setState({ isSaving: true });
        try {
            await this.persistSettings();
        } finally {
            this.saveLocked = false;
            this.setState({ isSaving: false });
        }
    };

    /**
     * 修改某一级菜单的极速点餐精确搜索规则。
     * @param value - 规则枚举值
     * @param menuId - 一级菜单 id
     */
    private onSelectMenuRule = (value: unknown, menuId: number): void => {
        const val = Number(value);
        this.setState({
            menuData: this.state.menuData.map((n) =>
                n.main_group_id === menuId ? { ...n, exact_search_rules: val } : n,
            ),
        });
    };

    /**
     * 修改全局「极速点餐精确搜索规则」（设置 id 23），稿面为左列首行下拉。
     * @param event - 来自 `CommonInput` 的变更事件
     */
    private onChangeFastSearchRuleSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.setState({ fastSearchRule: Number(event.target.value) });
    };

    /**
     * 修改就餐超时预警时间（分钟），不允许为负。
     */
    private onChangeDinnerWarning = (event: React.ChangeEvent<HTMLInputElement>): void => {
        let v = Number(Number(event.target.value).toFixed(2));
        if (Number.isNaN(v) || v < 0) {
            v = 0;
        }
        this.setState({ dinnerWarningMinutes: v });
    };

    /**
     * 修改 QR 扫码模式；无授权时禁止选外网。
     */
    private onChangeQrScanMode = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const next = Number(event.target.value);
        if (next === 2 && !this.state.hasPublicQopAuth) {
            GlobalNotification.error(i18n.t('cop_setting_page_qr_code_scan_error'));
            return;
        }
        this.setState({ qrScanMode: next });
    };

    /**
     * 将某一级菜单加入外卖菜单列表。
     * @param menuId - 一级菜单 id
     */
    private onAddTakeawayMenuId = (menuId: number): void => {
        if (this.state.takeawayMenuIds.includes(menuId)) {
            return;
        }
        this.setState({
            takeawayMenuIds: [...this.state.takeawayMenuIds, menuId],
        });
    };

    /**
     * 从外卖菜单列表移除某一级菜单。
     * @param menuId - 一级菜单 id
     */
    private onRemoveTakeawayMenuId = (menuId: number): void => {
        this.setState({
            takeawayMenuIds: this.state.takeawayMenuIds.filter((id) => id !== menuId),
        });
    };

    public render(): React.ReactNode {
        if (!this.state.initialPageDataReady) {
            return <RwaSettingPageSkeleton />;
        }
        const {
            menuData,
            fastSearchRule,
            dinnerWarningMinutes,
            qrScanMode,
            takeawayMenuIds,
            isSaving,
        } = this.state;
        const ruleItems = this.getRuleSelectItems();
        return (
            <Box className="rwa-setting-page">
                <Box className="rwa-setting-page__surface">
                    <Box className="rwa-setting-page__header">
                        <Typography variant="h4" color="primary" className="rwa-setting-page__page-title">
                            {i18n.t('rwa_setting_page_heading')}
                        </Typography>
                        <SubFunctionProjectSettingSaveButton loading={isSaving} onClick={this.onClickSave} />
                    </Box>

                    <Grid container spacing={3} className="rwa-setting-page__columns">
                        <Grid item xs={12} md={6}>
                            <Box className="rwa-setting-page__left-card">
                                <Typography variant="subtitle1" className="rwa-setting-page__block-title rwa-setting-page__section-title">
                                    {i18n.t('rwa_setting_page_search_menu_block_title')}
                                </Typography>
                                {this.context?.auxiliaryModeEnabled ? (
                                    <Typography variant="body2" className="rwa-setting-page__text-secondary">
                                        {i18n.t('rwa_setting_page_search_menu_block_subtitle')}
                                    </Typography>
                                ) : null}
                                <Box className="rwa-setting-page__menu-rule-row">
                                    <Typography
                                        variant="subtitle1"
                                        className="rwa-setting-page__row-label-text rwa-setting-page__menu-rule-row-label"
                                    >
                                        {i18n.t('rwa_setting_page_default_precise_search_rule')}
                                    </Typography>
                                    {/* 固定宽度容器：`BaseInput` 根节点为 width:100%，须约束 flex 子项宽度才能实现左文右下拉 */}
                                    <Box className="rwa-setting-page__menu-rule-row-control">
                                        <CommonInput
                                            className="rwa-setting-page__rule-select"
                                            inputType="SELECT"
                                            size="small"
                                            label=""
                                            value={String(fastSearchRule)}
                                            items={ruleItems.map((r) => ({ value: r.value, label: r.label }))}
                                            onChange={this.onChangeFastSearchRuleSelect}
                                        />
                                    </Box>
                                </Box>
                                <Box className="rwa-setting-page__divider" role="separator" />
                                <Box className="rwa-setting-page__menu-list">
                                    {menuData.map((menu) => (
                                        <Box key={menu.main_group_id} className="rwa-setting-page__menu-rule-row">
                                            <Typography
                                                variant="body1"
                                                className="rwa-setting-page__row-label-text rwa-setting-page__menu-name rwa-setting-page__menu-rule-row-label"
                                            >
                                                {menu.main_group_name}
                                            </Typography>
                                            <Box className="rwa-setting-page__menu-rule-row-control">
                                                <CommonInput
                                                    className="rwa-setting-page__rule-select"
                                                    inputType="SELECT"
                                                    size="small"
                                                    label=""
                                                    value={String(menu.exact_search_rules ?? '')}
                                                    items={ruleItems.map((r) => ({ value: r.value, label: r.label }))}
                                                    onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                                                        this.onSelectMenuRule(e.target.value, menu.main_group_id as number)
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box className="rwa-setting-page__right-card">
                                <Typography variant="subtitle1" className="rwa-setting-page__block-title rwa-setting-page__section-title">
                                    {i18n.t('base_setting_title')}
                                </Typography>
                                <Box className="rwa-setting-page__setting-row">
                                    <Stack spacing={1} className="rwa-setting-page__field-label-stack">
                                        <Typography variant="subtitle1" className="rwa-setting-page__row-label-text">
                                            {i18n.t('rwa_setting_page_dinner_warning_out_time')}
                                        </Typography>
                                        {this.context?.auxiliaryModeEnabled ? <Typography variant="body2" className="rwa-setting-page__text-secondary">
                                            {i18n.t('rwa_setting_aux_dinner_warning_figma')}
                                        </Typography>
                                            : null}
                                    </Stack>
                                    <Box className="rwa-setting-page__setting-control">
                                        <CommonInput
                                            className="rwa-setting-page__rule-select"
                                            inputType="INPUT"
                                            type="number"
                                            size="small"
                                            value={String(dinnerWarningMinutes)}
                                            onChange={this.onChangeDinnerWarning}
                                        />
                                    </Box>
                                </Box>
                                <Box className="rwa-setting-page__setting-row">
                                    <Stack spacing={1} className="rwa-setting-page__field-label-stack">
                                        <Typography variant="subtitle1" className="rwa-setting-page__row-label-text">
                                            {i18n.t('cop_setting_page_qr_code_scan')}
                                        </Typography>
                                        {this.context?.auxiliaryModeEnabled ? (
                                            <Typography variant="body2" className="rwa-setting-page__text-secondary">
                                                {i18n.t('figma_aux_qr_rwa_qop_connection')}
                                            </Typography>
                                        ) : null}
                                    </Stack>
                                    <Box className="rwa-setting-page__setting-control">
                                        <CommonInput
                                            className="rwa-setting-page__rule-select"
                                            inputType="SELECT"
                                            size="small"
                                            label=""
                                            value={qrScanMode}
                                            items={this.getQrScanSelectItems().map((o) => ({ value: o.value, label: o.label }))}
                                            onChange={this.onChangeQrScanMode}
                                        />
                                    </Box>
                                </Box>
                                <Typography
                                    variant="subtitle1"
                                    className="rwa-setting-page__block-title rwa-setting-page__takeaway-section-title"
                                >
                                    {i18n.t('rwa_setting_page_takeaway_menus')}
                                </Typography>
                                <FirstLevelMenuTagPicker
                                    selectedIds={takeawayMenuIds}
                                    allMenus={menuData}
                                    onAdd={this.onAddTakeawayMenuId}
                                    onRemove={this.onRemoveTakeawayMenuId}
                                    addButtonI18nKey="rwa_setting_page_add_takeaway_menu"
                                    removeChipAriaI18nKey="rwa_setting_page_takeaway_chip_remove_aria"
                                    listAriaLabelI18nKey="rwa_setting_page_takeaway_menus"
                                    borderedContainer
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        );
    }
}

export default RwaSettingPage;
